import { flightClock, flightDate } from './timezone.js';

/**
 * Flight — a read layer over the normalised JSON contract.
 *
 * The contract is columnar: one array per field, every array the same length.
 * That keeps the payload roughly four times smaller than an array of row
 * objects, which matters when fifty logs live in IndexedDB. The cost is that
 * reading a single instant needs an index into several arrays, so that cost is
 * paid once here rather than everywhere in the UI.
 *
 * Everything stored is SI. Conversion happens at the point of display, never
 * in the data.
 */

const EARTH_RADIUS_M = 6371000;

export class Flight {
  constructor(contract) {
    if (!contract || contract.version !== 1) {
      throw new Error(`Unsupported contract version: ${contract && contract.version}`);
    }

    this.raw = contract;
    this.meta = contract.meta;
    this.summary = contract.summary;
    this.cols = contract.cols;
    this.dictionaries = contract.dictionaries || {};
    this.events = contract.events || [];
    this.quality = contract.quality || {};
    this.count = contract.meta.rowCount;

    this._pathCache = null;
    this._eventsByIndex = null;
  }

  /** One instant, as a plain object. */
  row(i) {
    if (i < 0 || i >= this.count) return null;

    const out = {};
    for (const key in this.cols) out[key] = this.cols[key][i];
    out.index = i;
    out.flightMode = this.modeLabel(out.flightModeCode);
    return out;
  }

  series(name) {
    return this.cols[name] || null;
  }

  has(name) {
    const col = this.cols[name];
    if (!col) return false;
    for (let i = 0; i < col.length; i++) if (col[i] !== null) return true;
    return false;
  }

  /**
   * What to call this aircraft in a heading.
   *
   * The pilot's own name for it wins. Someone with six Mavics has named them
   * precisely so they can tell one flight from another, and "Mavic 3" on all
   * six rows tells them nothing. The official model is in the detail panel for
   * when it matters.
   */
  label() {
    return this.meta.aircraftName || this.meta.aircraftModel || 'Unknown aircraft';
  }

  modeLabel(code) {
    if (code === null || code === undefined) return null;
    const dict = this.dictionaries.flightMode || {};
    return dict[code] || `Mode ${code}`;
  }

  /**
   * Nearest row index for an elapsed time in seconds.
   * Binary search: the scrubber calls this on every pointer move.
   */
  indexAtTime(seconds) {
    const t = this.cols.timeS;
    if (!t || t.length === 0) return 0;

    let lo = 0;
    let hi = t.length - 1;

    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (t[mid] < seconds) lo = mid + 1;
      else hi = mid;
    }

    if (lo > 0 && Math.abs(t[lo - 1] - seconds) < Math.abs(t[lo] - seconds)) return lo - 1;
    return lo;
  }

  duration() {
    return this.summary.durationSeconds || 0;
  }

  /**
   * The flight path as [lon, lat, height] triples, gaps removed.
   * Longitude first, because that is what GeoJSON and MapLibre expect.
   */
  path() {
    if (this._pathCache) return this._pathCache;

    const { latitude, longitude, heightM } = this.cols;
    const points = [];

    for (let i = 0; i < this.count; i++) {
      const lat = latitude[i];
      const lon = longitude[i];
      if (lat === null || lon === null) continue;
      if (Math.abs(lat) < 0.0001 && Math.abs(lon) < 0.0001) continue;

      points.push([lon, lat, heightM[i] === null ? 0 : heightM[i], i]);
    }

    this._pathCache = points;
    return points;
  }

  /**
   * Simplified path for rendering. Full fidelity goes to the table; the map
   * gets geometry it can draw at sixty frames a second.
   */
  simplifiedPath(toleranceMetres = 1.5) {
    const points = this.path();
    if (points.length < 3) return points;
    return douglasPeucker(points, toleranceMetres);
  }

  /** Roughly `count` evenly spaced points, for the library thumbnail. */
  thumbnailPath(count = 40) {
    const points = this.path();
    if (points.length <= count) return points.map((p) => [p[1], p[0]]);

    const step = (points.length - 1) / (count - 1);
    const out = [];
    for (let i = 0; i < count; i++) {
      const p = points[Math.round(i * step)];
      out.push([p[1], p[0]]);
    }
    return out;
  }

  /**
   * Evenly spaced samples of the named columns. Feeds the AI digest, which
   * wants shape rather than every row.
   */
  sample(columns, count = 60) {
    const out = {};
    const step = Math.max(1, (this.count - 1) / (count - 1));

    for (const name of columns) {
      const col = this.cols[name];
      if (!col) continue;

      const values = [];
      for (let i = 0; i < count; i++) {
        const value = col[Math.min(this.count - 1, Math.round(i * step))];
        values.push(value === null ? null : round(value, 2));
      }
      out[name] = values;
    }

    out.sampleCount = count;
    return out;
  }

  /** Events whose span covers this row index. */
  eventsAt(i) {
    if (!this._eventsByIndex) this._buildEventIndex();
    return this._eventsByIndex.get(i) || [];
  }

  _buildEventIndex() {
    this._eventsByIndex = new Map();

    for (const event of this.events) {
      // Index the start only. A forty-row obstacle warning should toast once,
      // not forty times, which is the whole point of the run-length collapse
      // the parser already did.
      const list = this._eventsByIndex.get(event.index) || [];
      list.push(event);
      this._eventsByIndex.set(event.index, list);
    }
  }

  /** Events ordered for the log panel, most severe first within each instant. */
  timelineEvents() {
    const weight = { critical: 0, warning: 1, info: 2 };
    return [...this.events].sort(
      (a, b) => a.index - b.index || weight[a.severity] - weight[b.severity]
    );
  }

  /** A compact object for the library index record. */
  indexRecord(hash, payloadBytes) {
    return {
      hash: hash || this.meta.hash,
      filename: this.meta.filename,
      aircraftModel: this.meta.aircraftModel,
      aircraftName: this.meta.aircraftName,
      startEpoch: this.meta.startEpoch,
      flownAt: this.meta.flownAt,
      flownAtSource: this.meta.flownAtSource,
      timeZone: this.meta.timeZone || null,
      durationSeconds: this.summary.durationSeconds,
      distanceTotalM: this.summary.distanceTotalM,
      heightMaxM: this.summary.heightMaxM,
      batteryPercentStart: this.summary.battery.percentStart,
      batteryPercentEnd: this.summary.battery.percentEnd,
      hasPosition: this.quality.hasPosition === true,

      // Counted here rather than on render: the library list only holds index
      // records, and loading a whole payload to count its warnings would undo
      // the point of splitting them.
      warningCount: this.events.filter((e) => e.severity === 'warning').length,
      criticalCount: this.events.filter((e) => e.severity === 'critical').length,

      thumbnailPath: this.thumbnailPath(40),
      payloadBytes: payloadBytes || 0,
      parsedAt: this.meta.parsedAt,
    };
  }
}

/* ------------------------------------------------------------------ units */

/**
 * Display conversion. The data stays SI; only these functions know about feet.
 */
export const units = {
  system: 'metric',

  setSystem(system) {
    this.system = system === 'imperial' ? 'imperial' : 'metric';
  },

  distance(metres, decimals = 0) {
    if (metres === null || metres === undefined) return '—';
    if (this.system === 'imperial') {
      const feet = metres * 3.28084;
      if (feet >= 5280) return `${round(feet / 5280, 2)} mi`;
      return `${round(feet, decimals)} ft`;
    }
    if (metres >= 1000) return `${round(metres / 1000, 2)} km`;
    return `${round(metres, decimals)} m`;
  },

  altitude(metres, decimals = 1) {
    if (metres === null || metres === undefined) return '—';
    return this.system === 'imperial'
      ? `${round(metres * 3.28084, 0)} ft`
      : `${round(metres, decimals)} m`;
  },

  speed(ms, decimals = 1) {
    if (ms === null || ms === undefined) return '—';
    return this.system === 'imperial'
      ? `${round(ms * 2.23694, decimals)} mph`
      : `${round(ms, decimals)} m/s`;
  },

  temperature(celsius, decimals = 0) {
    if (celsius === null || celsius === undefined) return '—';
    return this.system === 'imperial'
      ? `${round(celsius * 1.8 + 32, decimals)}°F`
      : `${round(celsius, decimals)}°C`;
  },

  label(kind) {
    const metric = { distance: 'm', altitude: 'm', speed: 'm/s', temperature: '°C' };
    const imperial = { distance: 'ft', altitude: 'ft', speed: 'mph', temperature: '°F' };
    return (this.system === 'imperial' ? imperial : metric)[kind] || '';
  },
};

/* -------------------------------------------------------------- formatting */

export const fmt = {
  /** Elapsed time as m:ss, or h:mm:ss past an hour. */
  elapsed(seconds) {
    if (seconds === null || seconds === undefined) return '—';
    const total = Math.max(0, Math.floor(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
    return `${m}:${pad(s)}`;
  },

  /** Duration in words, for summary cards. */
  duration(seconds) {
    if (seconds === null || seconds === undefined) return '—';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    if (m === 0) return `${s}s`;
    return `${m}m ${pad(s)}s`;
  },

  /**
   * Wall clock from an epoch, in the flight's own time zone, with the zone
   * named. tz is meta.timeZone ({ name, abbreviation }) from the server.
   * Without one the time is shown in UTC, labelled, never in the reader's
   * zone: a time that changes with who is looking invites the wrong
   * conclusion. 24-hour, and midnight is 00 not 24 (see timezone.js).
   */
  clock(epochSeconds, withDate = false, tz = null) {
    return flightClock(epochSeconds, tz, withDate);
  },

  date(epochSeconds, tz = null) {
    return flightDate(epochSeconds, tz);
  },

  percent(value) {
    return value === null || value === undefined ? '—' : `${Math.round(value)}%`;
  },

  number(value, decimals = 1) {
    return value === null || value === undefined ? '—' : round(value, decimals).toString();
  },

  coordinate(lat, lon) {
    if (lat === null || lon === null) return '—';
    return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  },

  /**
   * Warning counts for a library row, or null when there is nothing to say.
   *
   * Returns data rather than markup so the logic can be tested without a DOM
   * and the presentation stays in the view.
   *
   * Null on a clean flight rather than a zero. A row reading "0 warnings" is
   * noise, and it trains the eye to skip a field that only matters when it is
   * not zero.
   */
  warningSummary(record) {
    const warnings = record.warningCount || 0;
    const criticals = record.criticalCount || 0;
    const total = warnings + criticals;

    if (total === 0) return null;

    const detail = [];
    if (criticals > 0) detail.push(`${criticals} critical`);
    if (warnings > 0) detail.push(`${warnings} warning${warnings === 1 ? '' : 's'}`);

    return {
      total,
      // Coloured by the worst present rather than showing two counts: a list
      // row has no room for a breakdown, so that goes in the tooltip.
      severity: criticals > 0 ? 'critical' : 'warning',
      label: `${total} warning${total === 1 ? '' : 's'}`,
      detail: detail.join(', '),
    };
  },

  bytes(n) {
    if (!n) return '0 KB';
    if (n >= 1048576) return `${round(n / 1048576, 1)} MB`;
    return `${Math.round(n / 1024)} KB`;
  },
};

/* ---------------------------------------------------------------- geometry */

export function haversine(lat1, lon1, lat2, lon2) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Douglas-Peucker on lon/lat, with the tolerance expressed in metres.
 *
 * Iterative rather than recursive: a 30-minute log at 10 Hz is 18,000 points
 * and deep recursion on a nearly-straight leg will blow the stack.
 */
function douglasPeucker(points, toleranceMetres) {
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;

  const stack = [[0, points.length - 1]];

  while (stack.length > 0) {
    const [first, last] = stack.pop();
    let maxDistance = 0;
    let maxIndex = 0;

    for (let i = first + 1; i < last; i++) {
      const d = perpendicularDistance(points[i], points[first], points[last]);
      if (d > maxDistance) {
        maxDistance = d;
        maxIndex = i;
      }
    }

    if (maxDistance > toleranceMetres && maxIndex > 0) {
      keep[maxIndex] = 1;
      stack.push([first, maxIndex], [maxIndex, last]);
    }
  }

  const out = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]);
  return out;
}

/**
 * Perpendicular distance in three dimensions, in metres.
 *
 * Altitude is part of the measurement, not an afterthought. Simplifying on
 * horizontal position alone means a vertical climb is a straight line seen
 * from above, so the whole ascent collapses into one segment and everything
 * derived from that segment takes the average of its endpoints. A drone
 * sitting on the ground before a 40 metre climb then has a path drawn 20
 * metres over its head.
 *
 * Vertical deviation in metres is directly comparable to horizontal deviation
 * in metres, so the third axis needs no weighting.
 */
function perpendicularDistance(point, lineStart, lineEnd) {
  // Local flat-earth projection. Over a single flight leg the error is far
  // below the tolerance we are testing against.
  const scale = Math.cos(toRad(lineStart[1]));

  const x = (point[0] - lineStart[0]) * scale * 111320;
  const y = (point[1] - lineStart[1]) * 110540;
  const z = (point[2] || 0) - (lineStart[2] || 0);

  const ex = (lineEnd[0] - lineStart[0]) * scale * 111320;
  const ey = (lineEnd[1] - lineStart[1]) * 110540;
  const ez = (lineEnd[2] || 0) - (lineStart[2] || 0);

  const lengthSquared = ex * ex + ey * ey + ez * ez;
  if (lengthSquared === 0) return Math.hypot(x, y, z);

  const t = Math.max(0, Math.min(1, (x * ex + y * ey + z * ez) / lengthSquared));

  return Math.hypot(x - t * ex, y - t * ey, z - t * ez);
}

/* ----------------------------------------------------------------- helpers */

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function pad(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

/** Always round before display. Float maths leaks 0.30000000000000004. */
export function round(value, decimals = 0) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
